import { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import worldTopo from "@/data/world-topo.json";

// ISO alpha-2 → numeric (UN M49) mapping for countries we track.
// TopoJSON from world-atlas uses numeric ISO codes as feature IDs.
const A2_TO_NUM = {
  IT: "380", AR: "032", AT: "040", AU: "036", BE: "056",
  BR: "076", CA: "124", CH: "756", CL: "152", CO: "170",
  CZ: "203", DE: "276", DK: "208", EG: "818", ES: "724",
  FR: "250", GB: "826", GR: "300", HR: "191", IE: "372",
  JP: "392", KR: "410", MA: "504", MX: "484", NG: "566",
  NL: "528", NO: "578", PL: "616", PT: "620", RO: "642",
  RS: "688", SE: "752", SN: "686", TR: "792", UA: "804",
  US: "840", UY: "858",
};

export default function WorldMap({ byCountry }) {
  const [hover, setHover] = useState(null);

  const numericCounts = useMemo(() => {
    const out = {};
    Object.entries(byCountry || {}).forEach(([code, count]) => {
      const num = A2_TO_NUM[code];
      if (num) out[num] = count;
    });
    return out;
  }, [byCountry]);

  const maxCount = Math.max(1, ...Object.values(numericCounts));
  const colorScale = scaleLinear()
    .domain([0, maxCount])
    .range(["#141A26", "#00F562"]);

  const numToA2 = useMemo(() => {
    const inv = {};
    Object.entries(A2_TO_NUM).forEach(([a2, num]) => (inv[num] = a2));
    return inv;
  }, []);

  return (
    <div className="relative">
      <ComposableMap
        data-testid="admin-world-map"
        projection="geoEqualEarth"
        projectionConfig={{ scale: 155 }}
        width={900}
        height={430}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={worldTopo}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const id = String(geo.id).padStart(3, "0");
              const count = numericCounts[id] || 0;
              const fill = count > 0 ? colorScale(count) : "#0D111A";
              const a2 = numToA2[id];
              const name = geo.properties.name;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() =>
                    setHover({ name, count, a2: a2 || "—" })
                  }
                  onMouseLeave={() => setHover(null)}
                  style={{
                    default: {
                      fill,
                      stroke: "#06080C",
                      strokeWidth: 0.4,
                      outline: "none",
                      transition: "fill 200ms",
                    },
                    hover: {
                      fill: count > 0 ? "#00F562" : "#1E293B",
                      stroke: "#00F562",
                      strokeWidth: 0.6,
                      outline: "none",
                      cursor: count > 0 ? "pointer" : "default",
                    },
                    pressed: { fill, outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {hover && (
        <div
          data-testid="admin-map-tooltip"
          className="pointer-events-none absolute left-4 top-4 max-w-xs rounded-xl border border-[#00F562]/30 bg-[#06080C]/95 px-4 py-3 shadow-xl backdrop-blur"
        >
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
            {hover.a2}
          </p>
          <p className="mt-1 font-display text-sm font-extrabold uppercase tracking-wide text-white">
            {hover.name}
          </p>
          <p className="mt-1 font-mono text-lg font-bold text-[#00F562] tabular-nums">
            {hover.count}{" "}
            <span className="text-[10px] uppercase tracking-widest text-slate-400">
              {hover.count === 1 ? "iscritto" : "iscritti"}
            </span>
          </p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-3">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
          Intensità:
        </span>
        <div className="flex items-center gap-1">
          {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
            <span
              key={i}
              className="h-3 w-7 rounded-sm"
              style={{ background: v === 0 ? "#141A26" : colorScale(v * maxCount) }}
            />
          ))}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 tabular-nums">
          0 → {maxCount}
        </span>
      </div>
    </div>
  );
}
